import { addDoc, collection, serverTimestamp, type CollectionReference } from 'firebase/firestore';
import type { SurveyPreset, SurveyQuestion, SurveyStatus } from '@citizen-walk/shared-types';
import { db } from '../../lib/firebase';

/**
 * Master preset → 地区 Survey の copy / link 取込 hook (Sprint BG-DataSettings 12、GAP-A9 / A-S16)。
 *
 * 旧 v1 `old/apps/admin/src/lib/surveys.ts::copyMasterSurveyToDistrict` /
 * `linkMasterSurveyToDistrict` を踏襲。
 *
 * - **copy**: preset の questions / title / description を deep clone し、独立した Survey を新規 doc
 *   として作成。以後 preset 側の変更は反映されない (sourceMasterSurveyId はトレース用に保持)。
 *   linkedToMaster = 'forked'、status は 'draft' に強制 (admin が地区側で公開判断)。
 * - **link**: 同じく Survey doc を新規作成するが、linkedToMaster = 'linked' + masterVersion を保持
 *   することで「再リンク (refresh)」候補と認識される (再リンク UI は別 sprint)。
 *
 * いずれも新規 docId は addDoc auto-id (旧 v1 では generateSurveyId)、戻り値は新規 surveyId。
 *
 * version は 1 (新規作成時)、responseCount は 0 (まだ回答なし)。
 *
 * `linkedToMaster: 'linked'` でも v2 は admin が地区側で別 doc に保管する pattern を踏襲した:
 *   - master preset 自体の不在化 (archive) リスクから守る
 *   - rules で district admin が write 可能な path に統一できる
 *   - 旧 v1 と同 schema (sourceMasterSurveyId + masterVersion)
 */

/** SurveyQuestion[] を deep clone (option / 配列も含めて) — 旧 cloneQuestions 踏襲。 */
export function cloneSurveyQuestions(questions: SurveyQuestion[]): SurveyQuestion[] {
  // structuredClone は jsdom で利用可、IE 等の古い env 想定不要 (v2 admin は modern browser のみ)。
  if (typeof structuredClone === 'function') {
    return structuredClone(questions);
  }
  // fallback: JSON シリアライズ (関数/Symbol 等は質問 schema に含まれないため安全)
  return JSON.parse(JSON.stringify(questions)) as SurveyQuestion[];
}

/** preset.title が空文字 / undefined のとき displayName を fallback として使う。 */
export function resolvePresetTitle(preset: Pick<SurveyPreset, 'title' | 'displayName'>): string {
  const t = preset.title?.trim();
  return t ? t : preset.displayName;
}

/**
 * preset を copy で地区 survey 化する (forked、以後独立)。
 * 戻り値: 新規 survey の id。
 *
 * @internal collectionRef は test mock で差し替え可能にするため optional injection。
 */
export async function copyPresetToDistrict(
  districtId: string,
  preset: SurveyPreset & { id: string },
  actorUid: string,
  collectionRef?: CollectionReference,
): Promise<string> {
  const ref = collectionRef ?? collection(db, 'districts', districtId, 'surveys');
  const created = await addDoc(ref, {
    schemaVersion: 1,
    title: resolvePresetTitle(preset),
    description: preset.description ?? '',
    status: 'draft' as SurveyStatus,
    questions: cloneSurveyQuestions(preset.questions ?? []),
    collectParticipantProfile:
      typeof preset.collectParticipantProfile === 'boolean'
        ? preset.collectParticipantProfile
        : true,
    allowEdit: typeof preset.allowEdit === 'boolean' ? preset.allowEdit : true,
    responseCount: 0,
    version: 1,
    sourceMasterSurveyId: preset.id,
    linkedToMaster: 'forked' as const,
    masterVersion: preset.version,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    createdBy: actorUid,
    updatedBy: actorUid,
  });
  return created.id;
}

/**
 * preset を link で地区 survey 化する (linked、再リンクで master 最新化候補)。
 * 戻り値: 新規 survey の id。
 *
 * 旧 v1 と同様、Survey doc は新規作成し、`linkedToMaster: 'linked'` + `masterVersion` を保持。
 * 「再リンク」UI (linked Survey に対する preset 最新版での再 sync) は別 sprint。
 */
export async function linkPresetToDistrict(
  districtId: string,
  preset: SurveyPreset & { id: string },
  actorUid: string,
  collectionRef?: CollectionReference,
): Promise<string> {
  const ref = collectionRef ?? collection(db, 'districts', districtId, 'surveys');
  const created = await addDoc(ref, {
    schemaVersion: 1,
    title: resolvePresetTitle(preset),
    description: preset.description ?? '',
    status: 'draft' as SurveyStatus,
    questions: cloneSurveyQuestions(preset.questions ?? []),
    collectParticipantProfile:
      typeof preset.collectParticipantProfile === 'boolean'
        ? preset.collectParticipantProfile
        : true,
    allowEdit: typeof preset.allowEdit === 'boolean' ? preset.allowEdit : true,
    responseCount: 0,
    version: 1,
    sourceMasterSurveyId: preset.id,
    linkedToMaster: 'linked' as const,
    masterVersion: preset.version,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    createdBy: actorUid,
    updatedBy: actorUid,
  });
  return created.id;
}

/**
 * 取込候補 preset を絞り込む helper:
 *   - archivedAt が null
 *   - enabled === true
 *   - status === 'published' (status 未設定の preset は許可 ─ 旧 v1 互換)
 *
 * UI から filter として呼び出される (Modal 側で適用)。
 */
export function selectImportablePresets(
  presets: Array<SurveyPreset & { id: string }>,
): Array<SurveyPreset & { id: string }> {
  return presets.filter((p) => {
    if (p.archivedAt) return false;
    if (p.enabled === false) return false;
    if (p.status && p.status !== 'published') return false;
    return true;
  });
}
